// The per-entity snapshot history the remote-interpolation clock reads from.
//
// Every remote plane keeps the last ~30 snapshots so `interpolateRemote` can
// find the two that straddle the render time and blend between them. That
// used to be an array of `{t, pos: Vector3, quat: Quaternion, vel: Vector3,
// ...}` objects with a `shift()` on the end: three heap objects per entity
// per snapshot, and an O(n) array walk to retire the oldest. With a full
// server that is a few thousand short-lived objects a second, produced
// forever, for planes that are mostly specks on the horizon.
//
// Here it is a true ring over typed arrays — a fixed allocation per entity,
// O(1) push, nothing retired and nothing collected.
//
// PRECISION, which is load-bearing:
//   - `t` is server time in SECONDS and lives in a Float64Array. Snapshot
//     times must keep millisecond resolution at long server uptimes, and
//     float32 does not (the wire format carries the same warning about its
//     own `now` field). Everything downstream of a mis-timed sample is a
//     stutter you cannot find later.
//   - position, velocity, quaternion and throttle are float32, which is
//     lossless for what arrives: positions come off the wire as f32 already,
//     and rotations/velocities as i16 quantised far coarser than f32.
//
// Samples read back through a scratch object the ring owns, so a lookup
// allocates nothing either. That scratch is per-ring and is reused by the
// NEXT read on the SAME ring — copy out what you need before looking again,
// the same rule `interpolateRemote`'s tmpV/tmpQ already follow.
import * as THREE from "three";

export const SNAP_RING_CAPACITY = 30;

/** One snapshot read out of the ring. Reused — never retained. */
class Sample {
  constructor() {
    this.t = 0;
    this.pos = new THREE.Vector3();
    this.quat = new THREE.Quaternion();
    this.vel = new THREE.Vector3();
    this.thr = 0;
    this.status = 0;
    this.plane = 0;
  }
}

export class SnapRing {
  constructor(capacity = SNAP_RING_CAPACITY) {
    this.capacity = capacity;
    this.count = 0;
    this.head = 0; // physical slot of logical index 0 (the oldest sample)
    this.t = new Float64Array(capacity);
    this.px = new Float32Array(capacity);
    this.py = new Float32Array(capacity);
    this.pz = new Float32Array(capacity);
    this.qx = new Float32Array(capacity);
    this.qy = new Float32Array(capacity);
    this.qz = new Float32Array(capacity);
    this.qw = new Float32Array(capacity);
    this.vx = new Float32Array(capacity);
    this.vy = new Float32Array(capacity);
    this.vz = new Float32Array(capacity);
    this.thr = new Float32Array(capacity);
    this.status = new Uint8Array(capacity);
    this.plane = new Uint8Array(capacity);
    // Two scratch samples: an interpolation always wants the pair either
    // side of the render time at once.
    this._a = new Sample();
    this._b = new Sample();
    this._turn = 0;
    // The newest sample, kept live as snapshots land. This one is NOT
    // scratch: it changes only when a new snapshot arrives, exactly like the
    // object `buffer.at(-1)` used to hand back, so the many callers that ask
    // "where is that plane right now" can hold it for the frame.
    this._newest = new Sample();
  }

  /** Physical slot for logical index i (0 = oldest). */
  _slot(i) {
    const s = this.head + i;
    return s >= this.capacity ? s - this.capacity : s;
  }

  /**
   * Append one snapshot, dropping the oldest once full. O(1) — no shift, no
   * allocation.
   */
  push(t, x, y, z, qx, qy, qz, qw, vx, vy, vz, thr, status, plane = 0) {
    let i;
    if (this.count < this.capacity) {
      i = this._slot(this.count);
      this.count++;
    } else {
      i = this.head;
      this.head = this.head + 1 >= this.capacity ? 0 : this.head + 1;
    }
    this.t[i] = t;
    this.px[i] = x; this.py[i] = y; this.pz[i] = z;
    this.qx[i] = qx; this.qy[i] = qy; this.qz[i] = qz; this.qw[i] = qw;
    this.vx[i] = vx; this.vy[i] = vy; this.vz[i] = vz;
    this.thr[i] = thr;
    this.status[i] = status;
    this.plane[i] = plane;
    const nw = this._newest;
    nw.t = t;
    nw.pos.set(x, y, z);
    nw.quat.set(qx, qy, qz, qw);
    nw.vel.set(vx, vy, vz);
    nw.thr = thr;
    nw.status = status;
    nw.plane = plane;
  }

  /** How many samples are held — `buffer.length` in the old array shape. */
  get length() {
    return this.count;
  }

  /** The newest sample, or null when nothing has landed yet. Stable for the
   *  frame: only a new snapshot moves it. */
  last() {
    return this.count ? this._newest : null;
  }

  /** Snapshot time at logical index i, without materialising the sample. */
  tAt(i) {
    return this.t[this._slot(i)];
  }

  /** Status at logical index i — the respawn-boundary test wants only this. */
  statusAt(i) {
    return this.status[this._slot(i)];
  }

  /**
   * Read logical index i into one of the ring's two scratch samples,
   * alternating between them so a caller can hold a straddling pair. Valid
   * until the second read after this one, on this ring.
   */
  read(i) {
    const out = (this._turn ^= 1) ? this._a : this._b;
    return this.readInto(i, out);
  }

  /** Read logical index i into a caller-owned sample. */
  readInto(i, out) {
    const s = this._slot(i);
    out.t = this.t[s];
    out.pos.set(this.px[s], this.py[s], this.pz[s]);
    out.quat.set(this.qx[s], this.qy[s], this.qz[s], this.qw[s]);
    out.vel.set(this.vx[s], this.vy[s], this.vz[s]);
    out.thr = this.thr[s];
    out.status = this.status[s];
    out.plane = this.plane[s];
    return out;
  }

  clear() {
    this.count = 0;
    this.head = 0;
  }
}

/** A standalone sample, for callers that need one that nothing else touches. */
export function makeSample() {
  return new Sample();
}
